/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import BulkBeaconInstall from './BulkBeaconInstall'
import FirmwareSettings from './FirmwareSettings'
import { CenteredLoader, ConditionalRender, StyledHeader, StyledMetaData } from 'components/widgets'
import { ExternalIntegrationsSettings } from './ExternalIntegrationsSettings'
import { BluetoothPairingSettings } from './BluetoothPairingSettings'
import { bluetoothPairingFlag } from 'lib/utils/featureFlags'
import { isSuperAdmin, isVoiceEnabled } from 'lib/utils/auth'
import { ALL_ORGS_SELECTED, BORDER1_COLOR } from 'lib/constants'
import { Organization, trans, TranslationGroup, TranslationKey } from 'lib/types'
import GeofenceSettings from './GeofenceSettings'
import VoiceSettings from './VoiceSettings'
import { useGlobal } from 'contexts/GlobalProvider'
import { getOrganization } from 'models/api'
import { useEffect, useState } from 'react'
import WifiNetworkSettings from './WifiNetworkSettings'
import WebhookSettings from './WebhookSettings'
import { Devices, Beacons } from 'models'

const StyledSettingsWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  fontFamily: 'Montserrat',
  lineHeight: '1.5em',
  button: { marginTop: '25px' },
})

const StyledSeparator = styled.div({
  border: `1px solid ${BORDER1_COLOR}`,
  marginTop: '38px',
  marginBottom: '40px',
})

// NEW: wrapper for org header so we don't use the css prop on a plain div
const OrgHeaderWrapper = styled.div({
  marginBottom: '16px',
})

export const GeneralSettingsContent = () => {
  const translation: TranslationGroup = trans.group(TranslationKey.HEADER)
  const [settingsOrg, setSettingsOrg] = useState<Organization | null>(null)
  const [ready, setReady] = useState(false)
  const { selectedOrgId } = useGlobal()
  const devices = Devices.use(({ devicesByOrgId }) => devicesByOrgId)

  type Device = { deviceType?: string | null }
  type DevicesByOrgId = Record<string, Device[] | undefined>

  function hasVoiceButtons(devicesByOrgId: DevicesByOrgId, selectedOrgId: string | undefined | null): boolean {
    if (!selectedOrgId) return false

    const orgDevices = devicesByOrgId?.[selectedOrgId] ?? []
    return orgDevices.some(d => d?.deviceType === 'voice_button')
  }

  // Load everything the page needs (selected org + devices + beacons) before
  // rendering, so sections don't flicker in one-by-one as each request lands.
  useEffect(() => {
    let alive = true
    setReady(false)

    const orgSelected = !!selectedOrgId && selectedOrgId !== ALL_ORGS_SELECTED
    const orgTask = orgSelected
      ? getOrganization(selectedOrgId as string).then(data => {
          if (alive) setSettingsOrg(data)
        })
      : Promise.resolve(setSettingsOrg(null))

    Promise.all([orgTask, Devices.reload(), Beacons.reload()]).finally(() => {
      if (alive) setReady(true)
    })

    return () => {
      alive = false
    }
  }, [selectedOrgId])

  return (
    <StyledSettingsWrapper>
      {!ready ? (
        <CenteredLoader />
      ) : (
        <>
          {settingsOrg && (
            <OrgHeaderWrapper>
              <StyledHeader>{settingsOrg.name}</StyledHeader>
              <StyledMetaData>id: {settingsOrg.id}</StyledMetaData>
            </OrgHeaderWrapper>
          )}

          <StyledHeader>{translation.settings}</StyledHeader>
          <FirmwareSettings />
          <ConditionalRender condition={isVoiceEnabled(settingsOrg)}>
            <StyledSeparator />
            <VoiceSettings org={settingsOrg} setSettingsOrg={setSettingsOrg} />
          </ConditionalRender>
          <StyledSeparator />
          <ConditionalRender condition={hasVoiceButtons(devices, selectedOrgId)}>
            <WifiNetworkSettings orgId={settingsOrg?.id} />
            <StyledSeparator />
          </ConditionalRender>
          <GeofenceSettings org={settingsOrg} setSettingsOrg={setSettingsOrg} />
          <StyledSeparator />
          <ConditionalRender condition={!!settingsOrg}>
            <WebhookSettings orgId={settingsOrg?.id} />
            <StyledSeparator />
          </ConditionalRender>
          <ConditionalRender condition={isSuperAdmin()}>
            <ExternalIntegrationsSettings org={settingsOrg} />
            <StyledSeparator />
          </ConditionalRender>
          <ConditionalRender condition={bluetoothPairingFlag && isVoiceEnabled(settingsOrg)}>
            <BluetoothPairingSettings org={settingsOrg} />
            <StyledSeparator />
          </ConditionalRender>

          <BulkBeaconInstall />
        </>
      )}
    </StyledSettingsWrapper>
  )
}
